import { useEffect, useRef } from 'react';
import { useStore } from 'zustand/react';
import { useLocation } from 'react-router-dom';
import { SortOrder, UseDynamicTableLogicParams } from '@models/table.model.ts';
import { useQueryParams } from '@utils/query-utils.ts';
import {
  shouldShowEmpty,
  shouldShowSetup
} from '@utils/dynamic-table.utils.ts';
import { ApiResponse } from '@models/api.model.ts';
import { SelectChangeEvent } from '@mui/material';
import { useTableColumnDnd } from '@components/dynamic-table/hooks/use-table-column-dnd.ts';

export function useDynamicTableLogic<T extends ApiResponse<any>>({
  columns,
  apiFunction,
  tableStore,
  language,
  onTriggerAction,
  setupForMe,
  setupLoading,
  initialState,
  tableKey = 'default'
}: UseDynamicTableLogicParams<T>) {
  const location = useLocation();
  const { getQueryParams, setQueryParams } = useQueryParams();
  const isInitialMount = useRef(true);
  const { orderedColumns, sensors, handleDragEnd } = useTableColumnDnd({
    columns,
    tableKey
  });

  const {
    setApiFunctionAndFetchData,
    updatePagination,
    updateFilters,
    updateDateRangeFilters,
    updateSearch,
    updateSort,
    clearSort,
    clearFilters,
    clearSearch,
    clearDateRangeFilters,
    clearAllFilters
  } = useStore(tableStore);

  const sort = useStore(tableStore, (state) => state.sort);
  const search = useStore(tableStore, (state) => state.search);
  const filters = useStore(tableStore, (state) => state.filters);
  const tableData = useStore(tableStore, (state) => state.tableData);
  const isLoaded = useStore(tableStore, (state) => state.isLoaded());
  const isLoading = useStore(tableStore, (state) => state.isLoading());
  const pagination = useStore(tableStore, (state) => state.pagination);
  const betweenDateRangeFilters = useStore(
    tableStore,
    (state) => state.between
  );

  const query = getQueryParams();
  const showSetup = shouldShowSetup(tableData, setupForMe, query);
  const showEmpty = shouldShowEmpty(tableData, query, setupForMe);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const query = getQueryParams();
    const page = Number(query.get('page')) || initialState?.page || 1;
    const limit = Number(query.get('limit')) || initialState?.limit || 10;

    const sortParam =
      query.get('sort') ||
      (initialState?.sort
        ? `${initialState.sort[0]},${initialState.sort[1]}`
        : '');

    let searchParam = query.get('search');
    if (
      (!searchParam || searchParam === '{}' || searchParam.trim() === '') &&
      initialState?.search
    ) {
      try {
        searchParam = JSON.stringify(initialState.search);
      } catch (e) {
        void e;
      }
    }

    let filtersParam = query.get('filters');
    if (
      (!filtersParam || filtersParam === '{}' || filtersParam.trim() === '') &&
      initialState?.filters
    ) {
      try {
        filtersParam = JSON.stringify(initialState.filters);
      } catch (e) {
        void e;
      }
    }

    let betweenParam = query.get('between');
    if (
      (!betweenParam || betweenParam === '{}' || betweenParam.trim() === '') &&
      initialState?.between
    ) {
      try {
        betweenParam = JSON.stringify(initialState.between);
      } catch (e) {
        void e;
      }
    }

    if (sortParam) {
      const [column, order] = sortParam.split(',').map((s) => s.trim());
      if (column && order) {
        updateSort(column, order as SortOrder);
      }
    }

    if (searchParam) {
      try {
        const parsedSearch = JSON.parse(searchParam);
        Object.entries(parsedSearch).forEach(([key, value]) => {
          updateSearch(key, String(value));
        });
      } catch (e) {
        void e;
      }
    }

    if (filtersParam) {
      try {
        const parsedFilters = JSON.parse(filtersParam);
        Object.entries(parsedFilters).forEach(([key, value]) => {
          updateFilters(key, value as string[]);
        });
      } catch (e) {
        void e;
      }
    }

    if (betweenParam) {
      try {
        const parsedBetween = JSON.parse(betweenParam);
        Object.entries(parsedBetween).forEach(([key, value]) => {
          if (
            value &&
            typeof value === 'object' &&
            'from' in value &&
            'to' in value
          ) {
            updateDateRangeFilters(key, value as { from: string; to: string });
          }
        });
      } catch (e) {
        void e;
      }
    }

    tableStore.setState((state) => ({
      ...state,
      pagination: { ...state.pagination, page, rowsPerPage: limit }
    }));

    setApiFunctionAndFetchData(apiFunction);
    isInitialMount.current = false;
  }, [apiFunction]);

  // Track changes to the URL search parameter (for navigation from notifications to the same page)
  useEffect(() => {
    if (isInitialMount.current) return;

    const query = new URLSearchParams(location.search);
    const searchParam = query.get('search');

    if (searchParam && searchParam !== '{}') {
      try {
        const parsedSearch = JSON.parse(searchParam);
        const currentSearch = tableStore.getState().search;

        const hasChanges = Object.entries(parsedSearch).some(
          ([key, value]) => currentSearch[key] !== String(value)
        );

        if (hasChanges) {
          Object.entries(parsedSearch).forEach(([key, value]) => {
            updateSearch(key, String(value));
          });
        }
      } catch (e) {
        void e;
      }
    }
  }, [location.search]);

  useEffect(() => {
    setQueryParams({
      page: pagination.page,
      limit: pagination.rowsPerPage,
      sort: sort[0] ? `${sort[0]},${sort[1]}` : '',
      search: JSON.stringify(search),
      filters: JSON.stringify(filters),
      between: JSON.stringify(betweenDateRangeFilters)
    });
  }, [pagination, sort, search, filters, betweenDateRangeFilters]);

  useEffect(() => {
    if (!tableRef.current) return;
    const container = tableRef.current;
    if (showEmpty || isLoading) {
      container.scrollLeft =
        (container.scrollWidth - container.clientWidth) / 2;
      container.style.scrollbarWidth = 'none';
    } else if (tableData?.length > 0) {
      container.scrollLeft = 0;
      container.style.scrollbarWidth = 'auto';
    }
  }, [showEmpty, tableData, isLoading]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    updatePagination(value, pagination.rowsPerPage);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    updatePagination(pagination.page, Number(event.target.value));
  };

  const handleSort = (columnId: string) => {
    const [currentColumn, currentOrder] = sort;
    const newOrder =
      currentColumn === columnId && currentOrder === SortOrder.Ascending
        ? SortOrder.Descending
        : SortOrder.Ascending;
    updateSort(columnId, newOrder);
  };

  const handleSearchChange = (columnId: string, value: string) => {
    const column = columns.find((col) => col.id === columnId);
    const isTranslated = column?.isTranslated;
    const searchKey = isTranslated ? `${columnId}.${language}` : columnId;
    updateSearch(searchKey, value);
  };

  const handleFilterChange = (columnId: string, value: string[]) => {
    updateFilters(columnId, value);
  };

  const handleDateRangeFilterChange = (
    columnId: string,
    value: [Date | null, Date | null]
  ) => {
    const filterValue = {
      from: value[0]?.toISOString() ?? '',
      to: value[1]?.toISOString() ?? ''
    };
    updateDateRangeFilters(columnId, filterValue);
  };

  return {
    orderedColumns,
    sensors,
    handleDragEnd,
    sort,
    search,
    filters,
    betweenDateRangeFilters,
    tableData,
    isLoaded,
    isLoading,
    pagination,
    showSetup,
    showEmpty,
    setupLoading,
    onTriggerAction,
    tableRef,
    handlePageChange,
    handleRowsPerPageChange,
    handleSort,
    handleSearchChange,
    handleFilterChange,
    handleDateRangeFilterChange,
    clearSort,
    clearFilters,
    clearSearch,
    clearDateRangeFilters,
    clearAllFilters
  };
}
